import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { categoriesStore, productsStore, Category, initializeCache } from '@/stores/localStorageStore';
import { Sparkles, Loader2, ArrowRight } from 'lucide-react';

// ===== Corner badge icons (small, solid style) =====
const CornerGrapeIcon = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 16 16" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="7" r="1.8" /><circle cx="10" cy="7" r="1.8" />
        <circle cx="4.5" cy="10" r="1.8" /><circle cx="8" cy="10" r="1.8" /><circle cx="11.5" cy="10" r="1.8" />
        <circle cx="6" cy="13" r="1.8" /><circle cx="10" cy="13" r="1.8" />
        <path d="M8 3V6" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 3C9 2 10 2 11 3" stroke="currentColor" strokeWidth="0.8" fill="none" />
    </svg>
);
const CornerDropletIcon = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 2L12 8C12 11 10 14 8 14C6 14 4 11 4 8L8 2Z" fill="currentColor" opacity="0.3" />
        <path d="M8 2L12 8C12 11 10 14 8 14C6 14 4 11 4 8L8 2Z" />
    </svg>
);
const CornerUtensilsIcon = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.3" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 2V8C4 9 5 10 6 10V14" strokeLinecap="round" />
        <path d="M6 2V5" strokeLinecap="round" /><path d="M4 5H6" />
        <path d="M10 2C10 2 13 4 13 7C13 8 12 9 11 9V14" strokeLinecap="round" />
    </svg>
);
const CornerGiftIcon = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.3" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="6" width="10" height="8" rx="1" />
        <rect x="2" y="4" width="12" height="2" rx="0.5" />
        <line x1="8" y1="4" x2="8" y2="14" /><path d="M8 4C7 2 5 2 6 4" /><path d="M8 4C9 2 11 2 10 4" />
    </svg>
);
const CornerBagIcon = ({ className = '' }: { className?: string }) => (
    <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.3" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="6" width="10" height="8" rx="1" />
        <path d="M6 6V4C6 3 7 2 8 2C9 2 10 3 10 4V6" strokeLinecap="round" />
    </svg>
);

// ===== Background grape vines (botanical line-art) =====
const GrapeVinesBackground = () => (
    <>
        {/* Subtle line-art grape vines (left) */}
        <div className="absolute bottom-0 left-0 w-[400px] sm:w-[500px] h-[400px] pointer-events-none opacity-[0.05] z-0 overflow-hidden">
            <svg viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary" preserveAspectRatio="xMinYMax meet">
                <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M -50 600 C 150 500 350 300 800 100" strokeWidth="4" />
                    <path d="M 0 620 C 200 520 400 320 850 120" strokeWidth="2" />
                    
                    <path d="M 300 420 C 100 200 -50 450 150 400 C 250 350 300 500 200 500 C 150 500 100 450 150 430" />
                    <path d="M 500 280 C 700 50 900 250 700 350 C 600 400 550 300 650 250 C 700 220 750 280 700 300" />
                    
                    <g transform="translate(280, 220) rotate(-45) scale(2)">
                        <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                        <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                    </g>

                    <g transform="translate(600, 350) rotate(15) scale(2.5)">
                        <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                        <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                    </g>

                    <g transform="translate(380, 500) rotate(-100) scale(1.5)">
                        <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                        <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                    </g>

                    <g transform="translate(180, 480) scale(2.5)">
                        <circle cx="0" cy="0" r="15" />
                        <circle cx="28" cy="8" r="16" />
                        <circle cx="52" cy="0" r="14" />
                        <circle cx="-18" cy="20" r="16" />
                        <circle cx="12" cy="30" r="18" />
                        <circle cx="42" cy="32" r="16" />
                        <circle cx="-5" cy="50" r="17" />
                        <circle cx="25" cy="55" r="18" />
                        <circle cx="55" cy="52" r="15" />
                        <circle cx="10" cy="78" r="16" />
                        <circle cx="40" cy="75" r="15" />
                        <circle cx="25" cy="100" r="15" />
                        <circle cx="4" cy="105" r="14" />
                        <circle cx="18" cy="125" r="14" />
                    </g>
                </g>
            </svg>
        </div>

        {/* Subtle line-art grape vines (right) */}
        <div className="absolute bottom-0 right-0 w-[400px] sm:w-[500px] h-[400px] pointer-events-none opacity-[0.05] z-0 overflow-hidden transform -scale-x-100">
            <svg viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary" preserveAspectRatio="xMinYMax meet">
                <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M -50 600 C 150 500 350 300 800 100" strokeWidth="4" />
                    <path d="M 0 620 C 200 520 400 320 850 120" strokeWidth="2" />
                    
                    <path d="M 300 420 C 100 200 -50 450 150 400 C 250 350 300 500 200 500 C 150 500 100 450 150 430" />
                    <path d="M 500 280 C 700 50 900 250 700 350 C 600 400 550 300 650 250 C 700 220 750 280 700 300" />
                    
                    <g transform="translate(280, 220) rotate(-45) scale(2)">
                        <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                        <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                    </g>

                    <g transform="translate(600, 350) rotate(15) scale(2.5)">
                        <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                        <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                    </g>

                    <g transform="translate(200, 480) scale(2.5)">
                        <circle cx="0" cy="0" r="15" />
                        <circle cx="28" cy="8" r="16" />
                        <circle cx="-18" cy="20" r="16" />
                        <circle cx="12" cy="30" r="18" />
                        <circle cx="42" cy="32" r="16" />
                        <circle cx="-5" cy="50" r="17" />
                        <circle cx="25" cy="55" r="18" />
                        <circle cx="10" cy="78" r="16" />
                        <circle cx="40" cy="75" r="15" />
                        <circle cx="25" cy="100" r="15" />
                    </g>
                </g>
            </svg>
        </div>
    </>
);

// ===== Category icon mapping =====
const CATEGORY_EMOJIS: Record<string, string> = {
    'Wina': '🍷',
    'Oliwy': '🫒',
    'Tradycyjne przekąski': '🥖',
    'Na prezent': '🎁',
    'Pozostałe produkty': '🧺',
};

const CategoryIcon = ({ name }: { name: string }) => {
    const emoji = CATEGORY_EMOJIS[name] || '🛍️';
    return (
        <div className="w-16 h-16 rounded-full bg-[#f4ece1] flex items-center justify-center text-3xl shadow-sm border border-white/50 transition-transform duration-300 group-hover:scale-110">
            {emoji}
        </div>
    );
};

// ===== Category icon & corner icon mapping =====
const getCategoryDetails = (categoryName: string) => {
    const lower = categoryName.toLowerCase();
    if (lower.includes('win')) return { icon: () => <CategoryIcon name="Wina" />, cornerIcon: CornerGrapeIcon };
    if (lower.includes('oliw') || lower.includes('oil') || lower.includes('italian')) return { icon: () => <CategoryIcon name="Oliwy" />, cornerIcon: CornerDropletIcon };
    if (lower.includes('przekas') || lower.includes('snack')) return { icon: () => <CategoryIcon name="Tradycyjne przekąski" />, cornerIcon: CornerUtensilsIcon };
    if (lower.includes('prezent') || lower.includes('gift')) return { icon: () => <CategoryIcon name="Na prezent" />, cornerIcon: CornerGiftIcon };
    return { icon: () => <CategoryIcon name="Pozostałe produkty" />, cornerIcon: CornerBagIcon };
};

const getCategoryOrderIndex = (categoryName: string) => {
    const lower = categoryName.toLowerCase();
    if (lower.includes('win')) return 0;
    if (lower.includes('oliw') || lower.includes('oil') || lower.includes('italian')) return 1;
    if (lower.includes('przekas') || lower.includes('snack')) return 2;
    if (lower.includes('prezent') || lower.includes('gift')) return 3;
    return 4;
};

const formatProductCount = (count: number, lang: string) => {
    if (lang === 'EN') return `${count} ${count === 1 ? 'product' : 'products'}`;
    if (count === 1) return '1 produkt';
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 10 || lastTwoDigits >= 20)) return `${count} produkty`;
    return `${count} produktów`;
};

const getCategoryUrl = (category: string) => {
    const seoMap: Record<string, string> = {
        'wines': 'wina', 'tradycyjne-przekaski': 'tradycyjne-przekaski',
        'italianProducts': 'oliwa', 'coffee': 'kawa',
        'gifts': 'zestawy', 'accessories': 'pozostale-produkty'
    };
    return seoMap[category] ? `/produkty/${seoMap[category]}` : `/produkty/${category}`;
};

// ===== Category Card Component =====
const CategoryCard = ({ tile, index, language, t, onClick, wide = false }: any) => {
    const Icon = tile.icon;
    const CornerIcon = tile.cornerIcon;

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
            className={`w-full flex justify-center ${wide ? 'md:col-span-2' : ''}`}
        >
            <Link
                to={getCategoryUrl(tile.category)}
                onClick={(e) => { e.preventDefault(); onClick(tile.category); }}
                className={`group relative flex flex-col items-center justify-center pt-10 pb-8 px-6 rounded-[28px] bg-[#FAF7F2] border border-[#E5DEC9] shadow-sm transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20 active:scale-[0.98] overflow-hidden w-full h-full ${wide ? 'max-w-[546px] lg:max-w-none' : 'max-w-[280px]'}`}
            >
                {/* Top Right Corner Badge */}
                <div className="absolute top-0 right-0 w-11 h-11 bg-primary rounded-tr-[27px] rounded-bl-[20px] flex items-center justify-center text-white z-20">
                    <CornerIcon className="w-4 h-4" />
                </div>

                {/* Circle Icon Container */}
                <div className="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-[#f0ebe2] mb-5 transition-transform duration-300 group-hover:scale-105">
                    <Icon className="h-11 w-11 text-[#9a7b4f]" />
                </div>

                {/* Title */}
                <h3 className="font-display text-[22px] font-bold text-foreground mb-1 text-center leading-tight">
                    {tile.label}
                </h3>

                {/* Product count */}
                <span className="text-sm text-muted-foreground font-medium mb-3">
                    {formatProductCount(tile.count, language)}
                </span>

                {/* Diamond divider */}
                <div className="flex items-center justify-center gap-1.5 mb-4 mt-auto">
                    <div className="w-1.5 h-1.5 rotate-45 bg-[#d4af37]/40" />
                    <div className="w-2 h-2 rotate-45 bg-[#d4af37]" />
                    <div className="w-1.5 h-1.5 rotate-45 bg-[#d4af37]/40" />
                </div>

                {/* Explore button */}
                <div className="flex items-center gap-2 text-primary font-medium text-sm transition-transform group-hover:translate-x-1">
                    {t('exploreCategory') || 'Odkryj'} <ArrowRight className="w-4 h-4" />
                </div>
            </Link>
        </motion.div>
    );
};

const CategoriesSection = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const initialCategories = categoriesStore.getAll();
    const [isLoading, setIsLoading] = useState(initialCategories.length === 0);
    const [categories, setCategories] = useState<Category[]>(initialCategories);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                if (categoriesStore.getAll().length === 0) {
                    setIsLoading(true);
                }
                await initializeCache();
                setCategories(categoriesStore.getAll());
            } catch (error) {
                console.error('Error loading categories:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadCategories();
    }, []);

    const tiles = categories.filter(cat => cat.showInNav !== false).map((cat) => {
        const productCount = productsStore.getAll().filter(p => p.category === cat.name && p.status !== 'draft').length;
        const details = getCategoryDetails(cat.name);
        return {
            label: language === 'EN' && cat.displayNameEn ? cat.displayNameEn : cat.displayName,
            category: cat.name,
            icon: details.icon,
            cornerIcon: details.cornerIcon,
            count: productCount,
        };
    }).sort((a, b) => getCategoryOrderIndex(a.category) - getCategoryOrderIndex(b.category));

    const handleCategoryClick = (category: string) => {
        navigate(getCategoryUrl(category));
    };

    if (isLoading) {
        return (
            <section className="py-24 bg-background">
                <div className="container mx-auto px-4 flex justify-center">
                    <Loader2 className="h-10 w-10 animate-spin text-primary/60" />
                </div>
            </section>
        );
    }

    if (tiles.length === 0) return null;

    return (
        <section className="py-16 md:py-24 bg-background relative overflow-hidden" id="categories">
            <GrapeVinesBackground />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
                <div className="mb-14 flex flex-col items-center text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/5 border border-primary/10 text-primary mb-4"
                    >
                        <Sparkles className="w-4 h-4" />
                        <span className="text-[13px] font-semibold tracking-wider uppercase">Kolekcja</span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-4xl sm:text-5xl md:text-[56px] font-bold tracking-tight text-foreground"
                    >
                        {t('whatWePrepared') || 'Co dla Ciebie przygotowaliśmy'}
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-3 my-6"
                    >
                        <div className="h-[1.5px] w-16 bg-primary/30" />
                        <div className="text-primary/70">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 5V2l-5.89 5.89"/><circle cx="16.6" cy="15.89" r="3"/><circle cx="8.11" cy="7.4" r="3"/><circle cx="12.35" cy="11.65" r="3"/><circle cx="13.91" cy="5.85" r="3"/><circle cx="18.15" cy="10.09" r="3"/><circle cx="6.56" cy="13.2" r="3"/><circle cx="10.8" cy="17.44" r="3"/><circle cx="5" cy="19" r="3"/></svg>
                        </div>
                        <div className="h-[1.5px] w-16 bg-primary/30" />
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="mx-auto max-w-2xl text-[15px] sm:text-base text-muted-foreground leading-relaxed"
                    >
                        Odkryj naszą starannie wyselekcjonowaną ofertę, która przeniesie Cię prosto do serca słonecznej Italii.
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8 justify-items-center">
                    {tiles.map((tile, index) => (
                        <CategoryCard
                            key={tile.category}
                            tile={tile}
                            index={index}
                            language={language}
                            t={t}
                            onClick={handleCategoryClick}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default CategoriesSection;
